import { useMemo, useState } from 'react';
import { Badge, Button, Group, Stack, Text, TextInput, Title } from '@mantine/core';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { useLeasesList } from '../../../queries/leases';
import { useProjectMembers } from '../../../queries/project-members';
import { useProjectStore } from '../../../project/project-store';
import { LeaseFormDrawer } from './LeaseFormDrawer';
import type { Lease } from '../../../types/api';

export function LeaseListPage() {
  const projectId = useProjectStore((s) => s.projectId);
  const role = useProjectStore((s) => s.projectRole);
  const canWrite = role === 'owner' || role === 'moderator' || role === 'editor';

  const [q, setQ] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Lease | null>(null);

  const filters = useMemo(() => ({ q: q.trim() || undefined, limit: 200 }), [q]);
  const { data: leases = [], isLoading } = useLeasesList(filters);
  const { data: members } = useProjectMembers(projectId);

  if (!projectId) {
    return (
      <Stack>
        <Title order={2}>Lease</Title>
        <Text c="dimmed">Pick a project to view its leases.</Text>
      </Stack>
    );
  }

  function openCreate() {
    setEditTarget(null);
    setDrawerOpen(true);
  }

  function openEdit(l: Lease) {
    setEditTarget(l);
    setDrawerOpen(true);
  }

  function lastUpdatedLabel(l: Lease): string {
    const name =
      members?.find((m) => m.user_id === l.updated_by_user_id)?.username ??
      l.updated_by_user_id ??
      '—';
    return `${name} · ${new Date(l.updated_at).toLocaleDateString()}`;
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Stack gap={4}>
          <Title order={2}>Lease</Title>
          <Text c="dimmed" size="sm">
            Project-scoped mineral/surface leases.
          </Text>
        </Stack>
        {canWrite && (
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
            New lease
          </Button>
        )}
      </Group>

      <Group>
        <TextInput
          leftSection={<IconSearch size={14} />}
          placeholder="Search lease #, lessor, lessee"
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
          w={320}
        />
      </Group>

      <DataTable
        withTableBorder
        highlightOnHover
        minHeight={150}
        fetching={isLoading}
        records={leases}
        idAccessor="id"
        noRecordsText="No leases yet"
        onRowClick={({ record }) => openEdit(record)}
        columns={[
          {
            accessor: 'lease_number',
            title: 'Lease #',
            render: (l) => (
              <Stack gap={0}>
                <Text size="sm" fw={500}>
                  {l.lease_number}
                </Text>
                {l.alt_lease_number && (
                  <Text size="xs" c="dimmed">
                    {l.alt_lease_number}
                  </Text>
                )}
              </Stack>
            ),
          },
          {
            accessor: 'lessor',
            title: 'Lessor / Lessee',
            render: (l) => (
              <Stack gap={0}>
                <Text size="sm">{l.lessor ?? '—'}</Text>
                <Text size="xs" c="dimmed">
                  {l.lessee ?? '—'}
                </Text>
              </Stack>
            ),
          },
          {
            accessor: 'county',
            title: 'County / State',
            render: (l) => (
              <Text size="sm">
                {[l.county, l.state].filter(Boolean).join(', ') || '—'}
              </Text>
            ),
          },
          {
            accessor: 'status',
            title: 'Status',
            width: 130,
            render: (l) =>
              l.status ? (
                <Badge variant="light" color="indigo">
                  {l.status}
                </Badge>
              ) : (
                <Text size="xs" c="dimmed">
                  —
                </Text>
              ),
          },
          {
            accessor: 'updated_at',
            title: 'Last updated',
            render: (l) => (
              <Text size="xs" c="dimmed">
                {lastUpdatedLabel(l)}
              </Text>
            ),
          },
        ]}
      />

      <LeaseFormDrawer
        opened={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditTarget(null);
        }}
        lease={editTarget}
      />
    </Stack>
  );
}
